/**
 * Barcode helpers for the store (kitchen flow, Lane A).
 *
 * Pure functions only — the camera decoder lives in scannerCamera.ts and the
 * USB-gun listener in hooks/useKeyboardWedgeScanner.ts. Mirrors the backend's
 * canonicalBarcode / parseBarcodeCell (handlers/stock_item_barcodes.go,
 * handlers/stock_import.go); keep the two in step.
 */

/**
 * Normalises a scanned or typed code the way the server stores it, or returns
 * null when it cannot be a barcode.
 *
 * - Surrounding whitespace goes (USB guns append Enter/Tab, sheets pad cells).
 * - A 12-digit UPC-A becomes its 13-digit EAN form. Camera decoders and USB
 *   guns disagree on which of the two they report for the same can, so one
 *   stored form lets both find it.
 * - Anything unsafe in a URL path (/ ? # % \), non-printable, non-ASCII or
 *   longer than 64 is refused.
 */
export function canonicalBarcode(raw: string): string | null {
  const code = raw.trim()
  if (!code || code.length > 64) return null
  for (const ch of code) {
    const c = ch.charCodeAt(0)
    if (c < 0x21 || c > 0x7e || '/?#%\\'.includes(ch)) return null
  }
  if (/^\d{12}$/.test(code)) return `0${code}`
  return code
}

/**
 * GS1 check digit test for EAN-8, UPC-A (12) and EAN-13. Used only to tell a
 * clean retail read from a garbled one; Code-128 has no such digit.
 */
export function hasValidGtinCheckDigit(code: string): boolean {
  if (!/^(\d{8}|\d{12}|\d{13})$/.test(code)) return false
  const digits = code.split('').map(Number)
  const check = digits.pop()!
  let sum = 0
  // Weights run 3,1,3,1… from the digit next to the check digit.
  for (let i = digits.length - 1, w = 3; i >= 0; i--, w = w === 3 ? 1 : 3) sum += digits[i] * w
  return (10 - (sum % 10)) % 10 === check
}

/**
 * A camera frame can decode to a plausible-looking but wrong retail code. For
 * EAN/UPC shapes we insist on the check digit; any other shape (Code-128 SKUs,
 * QR text) is taken as read.
 */
export function isPlausibleScan(code: string): boolean {
  if (/^\d+$/.test(code) && [8, 12, 13].includes(code.length)) return hasValidGtinCheckDigit(code)
  return code.length >= 3
}

export interface ParsedBarcode {
  barcode: string
  pack_qty: number
}

/** Splits an import cell "code;code*24" into canonical codes with pack sizes. */
export function parseBarcodeCell(cell: string): { codes: ParsedBarcode[]; errors: string[] } {
  const codes: ParsedBarcode[] = []
  const errors: string[] = []
  for (const part of cell.split(/[;\n|]/)) {
    const p = part.trim()
    if (!p) continue
    let code = p
    let pack = 1
    const star = p.lastIndexOf('*')
    if (star > 0 && star < p.length - 1 && /^\d+$/.test(p.slice(star + 1).trim())) {
      code = p.slice(0, star).trim()
      pack = Number(p.slice(star + 1).trim())
    }
    const canon = canonicalBarcode(code)
    if (!canon) {
      errors.push(`Barcode "${code}" is not valid`)
      continue
    }
    if (!(pack > 0)) {
      errors.push(`Barcode ${canon}: pack size must be more than 0`)
      continue
    }
    codes.push({ barcode: canon, pack_qty: pack })
  }
  return { codes, errors }
}

// ── USB "keyboard wedge" scanner detection ───────────────────────────────────

export interface WedgeOptions {
  /** Longest gap between two keystrokes of one scan. Humans type ~100ms+. */
  maxGapMs?: number
  /** Shortest code worth treating as a scan. */
  minLength?: number
  /** Longest wait between the last character and the closing Enter. */
  enterGapMs?: number
}

export const WEDGE_DEFAULTS: Required<WedgeOptions> = { maxGapMs: 35, minLength: 6, enterGapMs: 100 }

/**
 * Tells a USB barcode gun from a person typing. A gun "types" the whole code
 * in a burst — a few milliseconds between keys — and ends it with Enter; a
 * person is an order of magnitude slower. Feed every keydown; `feed` returns
 * the code when an Enter closes a fast-enough burst, else null.
 *
 * Pure (time is passed in) so it is unit-testable without a DOM.
 */
export class WedgeDetector {
  private buf = ''
  private last = 0
  private readonly opts: Required<WedgeOptions>
  /**
   * True when the key just fed began a new burst (the first character after a
   * pause). The hook snapshots the focused input at that moment, so a scan
   * that lands in a text box can be undone once it is recognised.
   */
  startedBurst = false

  constructor(opts: WedgeOptions = {}) {
    // An option passed as undefined (the hook forwards whatever it was given)
    // must fall back to its default, not erase it: with minLength undefined no
    // scan could ever be long enough, and every USB gun went silent.
    const set = Object.fromEntries(Object.entries(opts).filter(([, v]) => v !== undefined)) as WedgeOptions
    this.opts = { ...WEDGE_DEFAULTS, ...set }
  }

  feed(key: string, at: number): string | null {
    const gap = at - this.last
    this.last = at
    this.startedBurst = false
    if (key === 'Enter' || key === 'Tab') {
      const code = this.buf
      this.buf = ''
      // A burst followed by a human pressing Enter later is not a scan.
      if (gap > this.opts.enterGapMs) return null
      return code.length >= this.opts.minLength ? code : null
    }
    if (key.length !== 1) return null // Shift, Alt… are part of a burst, not a break
    // A slow key starts a fresh burst: whatever came before was a human.
    if (this.buf && gap > this.opts.maxGapMs) this.buf = ''
    this.startedBurst = this.buf === ''
    this.buf += key
    return null
  }

  reset(): void {
    this.buf = ''
  }
}
